import { useEffect } from 'react';
import { pageKeyFromRoute, trackFeatureAction } from '../lib/usageTracking';

const TRACKED_ROUTE_PREFIXES = [
  '/dashboard',
  '/campaigns',
  '/autopilot',
  '/budgets',
  '/knowledge',
  '/auth-accounts',
  '/platform-accounts',
];

type TrackedAction = 'create' | 'edit' | 'save' | 'delete' | 'publish' | 'submit' | 'sync' | 'refresh' | 'export_open' | 'download' | 'copy' | 'enable' | 'disable';

const actionMatchers: Array<{ action: TrackedAction; pattern: RegExp }> = [
  { action: 'export_open', pattern: /^(导出|导出报表|导出数据)/ },
  { action: 'download', pattern: /^(下载|下载文件)/ },
  { action: 'create', pattern: /^(新增|新建|创建|添加)/ },
  { action: 'edit', pattern: /^(编辑|修改)/ },
  { action: 'save', pattern: /^(保存|保存设置)/ },
  { action: 'delete', pattern: /^(删除|移除)/ },
  { action: 'publish', pattern: /^(发布|提交发布)/ },
  { action: 'submit', pattern: /^(提交|确认)/ },
  { action: 'sync', pattern: /^(同步|刷新同步)/ },
  { action: 'refresh', pattern: /^(刷新|重新加载)/ },
  { action: 'enable', pattern: /^(启用|开启)/ },
  { action: 'disable', pattern: /^(停用|关闭)/ },
  { action: 'copy', pattern: /^(复制|拷贝)/ },
];

function actionForLabel(label: string): TrackedAction | undefined {
  return actionMatchers.find(item => item.pattern.test(label))?.action;
}

function controlLabel(element: Element): string {
  return (element.getAttribute('aria-label') || element.getAttribute('title') || element.textContent || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80);
}

/**
 * 只采集有明确业务语义的按钮，避免把普通表格点击、展开收起等高频行为写入日志。
 * 查询与重置由 DashboardQueryActions 单独标记，导出任务由下载中心补充“提交导出”。
 */
export function UsageActionTracker() {
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (event.button !== 0 || event.defaultPrevented) return;
      const origin = event.target;
      if (!(origin instanceof Element)) return;
      const control = origin.closest('button,[role="button"],a');
      if (!(control instanceof HTMLElement) || control.dataset.usageTracked === 'true' || control.hasAttribute('disabled')) return;

      const route = window.location.pathname;
      if (!TRACKED_ROUTE_PREFIXES.some(prefix => route === prefix || route.startsWith(`${prefix}/`))) return;

      const label = controlLabel(control);
      const action = actionForLabel(label);
      if (!action) return;

      void trackFeatureAction({
        page_key: pageKeyFromRoute(route),
        feature_key: 'page_action',
        action,
        route,
        properties: { control_label: label, control_tag: control.tagName.toLowerCase() },
      });
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  return null;
}
